import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Trans } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Tooltip from '@components/Tooltip';
import HelpIcon from '@mui/icons-material/Help';
import { Input } from '@components/Input';
import PhoneInput from '@components/PhoneInput';
import TeamSelect from '@features/TeamSelect';
import ChannelLocationSelect from '@features/ChannelLocationSelect';
import { Select } from '@components/Select';
import { Typography } from '@components/Typography';
import { InternalLink } from '@components/Link';
import { BASE_URL, handleApiError } from '@helpers/api';
import { CHANNEL, SmsProviderType } from '@constants/channels';
import { AccIntegrationName } from '@constants/integrations';
import { RoutePath } from '@constants/routes';
import { useTranslation, useCopyToClipboard, useThrottle } from '@hooks';
import { phoneDigits, setInitialPhoneNumberToForm } from '@helpers/phone';
import { getActiveIntegrations } from '@helpers/integrations';
import { ringcentralIntegrationService } from '@services';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const Container = styled('div')({
    display: 'grid',
    gridGap: '16px 24px',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    '& .team-select': {
        gridColumn: '1 / span 2',
    },
    '& .full-row': {
        gridColumn: '1 / span 2',
    },
    '@media (max-width: 600px)': {
        gridTemplateColumns: '100%',
        '& .team-select, & .full-row': {
            gridColumn: '1 / span 1',
        },
    },
});

const providersSendingFromTheNumber = [SmsProviderType.Twilio, SmsProviderType.RingCentral];

const providerTypeOptions = [
    { value: SmsProviderType.Twilio, label: 'Twilio' },
    { value: SmsProviderType.TurboSms, label: 'TurboSMS' },
    { value: SmsProviderType.Lifecell, label: 'Lifecell' },
    { value: SmsProviderType.SmsTo, label: ' SMS.to' },
];

const SmsForm = ({ accountLocale, isEdit, twillioUid, selectedChannel }) => {
    const { t } = useTranslation();
    const { onCopy } = useCopyToClipboard();
    const { control, setValue, getValues, watch } = useFormContext();
    const isMobile = useMediaQuery('@media (max-width: 600px)');

    const account = useSelector(state => state.account?.account);
    const channels = useSelector(state => state.channels.data);
    const numbersHeldByOtherSmsChannels = useMemo(
        () =>
            new Set(
                channels
                    .filter(
                        channel =>
                            channel.type === CHANNEL.SMS &&
                            channel.id !== selectedChannel?.id &&
                            providersSendingFromTheNumber.includes(channel.smsParameters?.provider),
                    )
                    .map(channel => phoneDigits(channel.smsParameters?.phoneNumber))
                    .filter(Boolean),
            ),
        [channels, selectedChannel?.id],
    );
    const ringCentralIntegrations = useMemo(
        () => getActiveIntegrations(account, AccIntegrationName.RingCentral),
        [account],
    );

    const selectedProvider = watch('provider');
    const selectedIntegrationId = watch('integrationId');
    const isCustomGateway = isEdit && selectedChannel?.smsParameters?.provider === SmsProviderType.Custom;
    const isSavedRingCentral = isEdit && selectedChannel?.smsParameters?.provider === SmsProviderType.RingCentral;
    const isRingCentral = selectedProvider === SmsProviderType.RingCentral;
    const ringCentralDisconnected =
        isSavedRingCentral && !ringCentralIntegrations.some(x => x.id === selectedChannel?.integrationId);
    const selectedIntegrationActive = ringCentralIntegrations.some(x => x.id === selectedIntegrationId);
    const providerOptions = [
        ...providerTypeOptions,
        ...(ringCentralIntegrations.length || isSavedRingCentral
            ? [{ value: SmsProviderType.RingCentral, label: 'RingCentral' }]
            : []),
        ...(isCustomGateway ? [{ value: SmsProviderType.Custom, label: t('SettingsChannels.customGateway') }] : []),
    ];

    const [ringCentralNumbers, setRingCentralNumbers] = useState([]);
    const [ringCentralNumbersLoading, setRingCentralNumbersLoading] = useState(false);
    const [ringCentralNumbersFailed, setRingCentralNumbersFailed] = useState(false);

    useEffect(() => {
        if (isRingCentral && !selectedIntegrationId && ringCentralIntegrations.length === 1) {
            setValue('integrationId', ringCentralIntegrations[0].id, { shouldValidate: true });
        }
    }, [isRingCentral, selectedIntegrationId, ringCentralIntegrations, setValue]);

    useEffect(() => {
        if (!isRingCentral || !selectedIntegrationActive) {
            setRingCentralNumbers([]);
            return undefined;
        }
        let cancelled = false;
        setRingCentralNumbersLoading(true);
        setRingCentralNumbersFailed(false);
        ringcentralIntegrationService
            .getPhoneNumbers(selectedIntegrationId)
            .then(numbers => {
                if (cancelled) return;
                const available = numbers || [];
                setRingCentralNumbers(available);
                const current = getValues('phoneNumber');
                if (current && !available.some(x => x.phoneNumber === current)) {
                    setValue('phoneNumber', '', { shouldValidate: true });
                }
            })
            .catch(error => {
                if (cancelled) return;
                setRingCentralNumbers([]);
                setRingCentralNumbersFailed(true);
                handleApiError(error);
            })
            .finally(() => {
                if (!cancelled) setRingCentralNumbersLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isRingCentral, selectedIntegrationId, selectedIntegrationActive, getValues, setValue]);

    useEffect(() => {
        if (isRingCentral && numbersHeldByOtherSmsChannels.has(phoneDigits(getValues('phoneNumber')))) {
            setValue('phoneNumber', '', { shouldValidate: true });
        }
    }, [isRingCentral, numbersHeldByOtherSmsChannels, getValues, setValue]);

    const ringCentralNumberOptions = useMemo(
        () =>
            ringCentralNumbers
                .filter(x => !numbersHeldByOtherSmsChannels.has(phoneDigits(x.phoneNumber)))
                .map(x => ({ value: x.phoneNumber, label: x.phoneNumber })),
        [ringCentralNumbers, numbersHeldByOtherSmsChannels],
    );

    const [onCopyClick] = useThrottle(value => onCopy(value), 1000);

    useEffect(() => {
        if (!isEdit) {
            setValue('phoneNumber', setInitialPhoneNumberToForm('', accountLocale), {
                shouldValidate: false,
                shouldTouch: true,
            });
        }
    }, [setValue, accountLocale]);

    return (
        <Container>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('SettingsChannels.channelName')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                    />
                )}
            />
            <Controller
                control={control}
                name="provider"
                render={({ field, fieldState: { error }, ref }) => (
                    <Select
                        {...field}
                        ref={ref}
                        onChange={({ target: { value } }) => {
                            const leavesRingCentral = getValues('provider') === SmsProviderType.RingCentral;
                            setValue('provider', value, { shouldDirty: true });
                            if (value === SmsProviderType.RingCentral) {
                                setValue('phoneNumber', '', { shouldValidate: true });
                            } else if (leavesRingCentral) {
                                setValue('integrationId', '', { shouldValidate: true });
                                setValue('phoneNumber', setInitialPhoneNumberToForm('', accountLocale), {
                                    shouldValidate: true,
                                });
                            }
                        }}
                        options={providerOptions}
                        label={t('SettingsChannels.provider')}
                        fullWidth
                        required
                        disabled={isEdit}
                        error={!!error}
                    />
                )}
            />
            <TeamSelect data={selectedChannel} />
            {isRingCentral && ringCentralDisconnected && !selectedIntegrationActive && (
                <Typography variant="caption" className="full-row" sx={{ color: 'var(--error-main)' }}>
                    <Trans t={t} i18nKey="SettingsChannels.ringCentralDisconnected">
                        <InternalLink to={RoutePath.AccountSettingsIntegrations} variant="caption" underline="hover">
                            {{ link: t('SettingsChannels.ringCentralIntegrations') }}
                        </InternalLink>
                    </Trans>
                </Typography>
            )}
            {isRingCentral &&
                ringCentralIntegrations.length > 0 &&
                (ringCentralIntegrations.length > 1 || ringCentralDisconnected) && (
                    <Controller
                        control={control}
                        name="integrationId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Select
                                {...field}
                                value={selectedIntegrationActive ? field.value : ''}
                                ref={ref}
                                onChange={({ target: { value } }) => {
                                    setValue('integrationId', value, { shouldValidate: true, shouldDirty: true });
                                    setValue('phoneNumber', '', { shouldValidate: true });
                                }}
                                options={ringCentralIntegrations.map(x => ({
                                    value: x.id,
                                    label: x.accountLabel || x.userIdentifier,
                                }))}
                                label={t('SettingsChannels.ringCentralAccount')}
                                fullWidth
                                required
                                error={!!error}
                            />
                        )}
                    />
                )}
            {isRingCentral && ringCentralIntegrations.length > 0 && (
                <Controller
                    control={control}
                    name="phoneNumber"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            value={ringCentralNumberOptions.some(x => x.value === field.value) ? field.value : ''}
                            ref={ref}
                            onChange={({ target: { value } }) => {
                                setValue('phoneNumber', value, { shouldValidate: true, shouldDirty: true });
                            }}
                            options={ringCentralNumberOptions}
                            label={t('SettingsChannels.ringCentralNumber')}
                            fullWidth
                            required
                            disabled={!selectedIntegrationActive || ringCentralNumbersLoading}
                            error={!!error}
                            helperText={
                                !ringCentralNumbersLoading &&
                                !ringCentralNumbersFailed &&
                                selectedIntegrationActive &&
                                !ringCentralNumberOptions.length
                                    ? t(
                                          ringCentralNumbers.length
                                              ? 'SettingsChannels.ringCentralNumbersInUse'
                                              : 'SettingsChannels.ringCentralNoNumbers',
                                      )
                                    : null
                            }
                        />
                    )}
                />
            )}
            {selectedProvider === SmsProviderType.Twilio && (
                <>
                    <Controller
                        control={control}
                        name="accountId"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsChannels.accountSid')}
                                type="password"
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="apiKey"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('SettingsChannels.authToken')}
                                type="password"
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="phoneNumber"
                        render={({ field, fieldState: { error } }) => (
                            <PhoneInput
                                placeholder=""
                                label={t('common.phoneNumber')}
                                inputProps={{ name: 'phoneNumber' }}
                                value={field.value}
                                autoFormat
                                required
                                onChange={value => {
                                    setValue('phoneNumber', value, { shouldValidate: true, shouldDirty: true });
                                }}
                                error={error ? t(error.message) : null}
                            />
                        )}
                    />
                </>
            )}
            {[SmsProviderType.TurboSms, SmsProviderType.SmsTo].includes(selectedProvider) && (
                <Controller
                    control={control}
                    name="token"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsChannels.apiToken')}
                            type="password"
                            fullWidth
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                            required
                        />
                    )}
                />
            )}
            {selectedProvider === SmsProviderType.Lifecell && (
                <>
                    <Controller
                        control={control}
                        name="login"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('common.login')}
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="password"
                        render={({ field, fieldState: { error }, ref }) => (
                            <Input
                                ref={ref}
                                inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                label={t('common.password')}
                                type="password"
                                fullWidth
                                error={!!error}
                                helperText={error ? t(error.message) : null}
                                required
                            />
                        )}
                    />
                </>
            )}
            {selectedProvider !== SmsProviderType.Custom && !isRingCentral && (
                <Controller
                    control={control}
                    name="senderId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Input
                            ref={ref}
                            inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                            label={t('SettingsChannels.senderId')}
                            fullWidth
                            error={!!error}
                            helperText={
                                error ? (
                                    <div style={{ position: 'absolute', top: '-14px', width: '250px' }}>
                                        {t(error.message, { count: 11 })}
                                    </div>
                                ) : null
                            }
                            required={selectedProvider !== SmsProviderType.Twilio}
                            addonComponent={
                                <Tooltip title={t('SettingsChannels.senderIdNotice')} placement="right">
                                    <HelpIcon sx={{ fontSize: '16px' }} />
                                </Tooltip>
                            }
                        />
                    )}
                />
            )}
            {selectedProvider === SmsProviderType.Twilio && (
                <Input
                    inputProps={{ value: `${BASE_URL}api/inbox/webhooks/twilio-message/${twillioUid}` }}
                    onAddonClick={() => onCopyClick(`${BASE_URL}api/inbox/webhooks/twilio-message/${twillioUid}`)}
                    addonComponent={<ContentCopyIcon sx={{ fontSize: 18, color: 'var(--text-secondary)' }} />}
                    label="Webhook URL"
                    disabled
                    sx={{ gridColumn: isMobile ? '1 / span 1' : '1 / span 2' }}
                />
            )}
            <ChannelLocationSelect data={selectedChannel} />
        </Container>
    );
};

export default SmsForm;
